import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import TextField, { MenuProps } from 'components/textfield'
import { topBarPopoverOverride } from 'application/theme'
import MenuItem from '@material-ui/core/MenuItem'
import Box from '@material-ui/core/Box'
import Checkbox from '@material-ui/core/Checkbox'
import ListItemText from '@material-ui/core/ListItemText'
import Chip from '@material-ui/core/Chip'

const useStyles = makeStyles(theme => ({
  dropdown: {
    // Width 180 allows 3 dropdowns to fit in the top bar on a medium width screen.
    // width: 180
    minWidth: 160
  },
  selectDropdown: {
    cursor: 'initial !important',
    position: 'relative'
  },
  // Because we display our popovers in the global portal, we have to style
  // them correctly.
  whiteDropdown: {
    ...topBarPopoverOverride
  },
  denseGutters: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(2)
  },
  chipRoot: {
    position: 'absolute',
    top: theme.spacing(1.3),
    background: theme.palette.background.bg3,
    zIndex: theme.zIndex.appBar - 1
  }
}))

const Dropdown = ({ disabled, value, label, onChange, values, multiple }) => {
  const classes = useStyles()
  const container = useRef()
  const [open, setOpen] = useState(false)

  const onChangeBuffer = useCallback((e) => {
    onChange(e.target.value)
  }, [onChange])

  const onRender = useCallback((val) => {
    return <Chip
      classes={{ root: classes.chipRoot }}
      size="small" label={`${val.length} selected`}
      onDelete={() => {
        onChange(null)
        setOpen(false)
      }}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onMouseDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }} />
  }, [onChange, setOpen, classes])

  useEffect(() => {
    if (!open) document.activeElement.blur()
  }, [open])

  value = multiple ? (value || []) : (value || 0)

  return (
    <Box className={classes.dropdown}>
      <TextField
        select
        margin='none'
        variant='outlined'
        fullWidth
        InputProps={{ classes: { root: classes.selectDropdown } }}
        SelectProps={{
          renderValue: multiple ? onRender : null, multiple,
          onClose: () => setOpen(false),
          onOpen: () => setOpen(true),
          open,
          MenuProps: {
            ...MenuProps,
            PopoverClasses: { paper: classes.whiteDropdown },
            disablePortal: true,
            container: (container.current),
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            anchorReference: 'anchorEl',
            anchorEl: (container.current)
          }
        }}
        label={label}
        disabled={disabled}
        value={value}
        onChange={onChangeBuffer}>
        {multiple ? null : <MenuItem value={0}><span>All</span></MenuItem>}

        {values.map((option, index) => (
          <MenuItem key={`${option.id}-${index}`} value={option.id} classes={{ gutters: multiple ? classes.denseGutters : null }} dense>
            {multiple
              ? <Box display="flex" alignItems="center"><Checkbox checked={value.indexOf(option.id) > -1} />&nbsp;<ListItemText primary={option.name} /></Box>
              : <span>{option.name}</span>}
          </MenuItem>
        ))}
      </TextField>
      <Box ref={container} />
    </Box>
  )
}

export const BuildingFilter = ({ building, setBuilding, DropdownProps, companyType }) => {
  const loading = useSelector((state) => false)
  const buildings = useSelector((state) => state.workspace.buildings)

  const filteredBuildings = useMemo(() => {
    return buildings.filter((building) => (!building.archive)).sort((a, b) => a.name.localeCompare(b.name))
  }, [buildings])

  return <Dropdown
    disabled={loading}
    value={building || 0}
    values={filteredBuildings}
    onChange={(value) => setBuilding(value)}
    label={String(companyType) === '3' ? 'Property' : 'Building'}
    {...DropdownProps} />
}

export const FloorFilter = ({ floor, setFloor, building, DropdownProps, companyType }) => {
  //Get the user's company from the state
  const companyId = useSelector(state => state.auth.companyId)
  const loading = useSelector((state) => false)
  const floors = useSelector((state) => state.workspace.floors)

  building = (Array.isArray(building) ? building : [building]).filter((i) => i)

  const filteredFloors = useMemo(() => {
    return floors.filter((floor) => (building.includes(floor.building)))
  }, [floors, building])

  const sorted = (building.length === 0 ? floors : filteredFloors).sort((a, b) => a.name.localeCompare(b.name)).filter(v => !v.archive)

  return <Dropdown
    disabled={loading}
    value={floor || 0}
    values={sorted}
    onChange={(value) => setFloor(value)}
    label={String(companyType) === '3' ? 'Block' : 'Floor'}
    {...DropdownProps} />
}

export const AreaFilter = ({ area, setArea, building, floor, DropdownProps, onlyRoom, onlyDesk }) => {
  const loading = useSelector(() => false)
  let areas = useSelector((state) => state.workspace.areas)

  building = (Array.isArray(building) ? building : [building]).filter((i) => i)
  floor = (Array.isArray(floor) ? floor : [floor]).filter((i) => i)

  const filteredAreas = useMemo(() => {
    if (building.length > 0) areas = areas.filter(a => building.includes(a.building))
    if (floor.length > 0) areas = areas.filter(a => floor.includes(a.floor))
    return areas
  }, [areas, floor, building])

  let sorted = (floor.length === 0 && building.length === 0 ? areas : filteredAreas)
    .sort((a, b) => a.name.localeCompare(b.name)).filter(v => !v.archive)

  if (onlyRoom) sorted = sorted.filter(v => v.capacity > 0)
  if (onlyDesk) sorted = sorted.filter(v => v.capacity === 0)

  return <Dropdown
    disabled={loading}
    value={area || 0}
    values={sorted}
    onChange={(value) => setArea(value)}
    label="Area"
    {...DropdownProps} />
}
